import { ActionIcon, Box, Button, Group, Select, Textarea, Tooltip } from '@mantine/core'
import { Paperclip, Play, Send } from 'lucide-react'
import type { FormEvent, KeyboardEvent } from 'react'
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react'
import VoiceInputButton from '@/components/VoiceInputButton'
import type { ModelGroup } from '@/types'
import type { VoiceRecorderState } from '@/lib/useVoiceRecorder'

export type ChatInputBoxHandle = {
  clear: () => void
  appendText: (text: string) => void
}

type ChatInputBoxProps = {
  onSubmit: (text: string, options?: { headless?: boolean }) => void
  disabled?: boolean
  loading?: boolean
  availableModels: ModelGroup[]
  selectedModel: string
  onModelChange: (model: string) => void
  voiceState: VoiceRecorderState
  levelHistory: number[]
  onVoiceToggle: () => void
  ready: boolean
  backgroundEligible: boolean
  autoFocus?: boolean
}

const ChatInputBox = forwardRef<ChatInputBoxHandle, ChatInputBoxProps>(function ChatInputBox({
  onSubmit,
  disabled,
  loading,
  availableModels,
  selectedModel,
  onModelChange,
  voiceState,
  levelHistory,
  onVoiceToggle,
  ready,
  backgroundEligible,
  autoFocus,
}, ref) {
  const [input, setInput] = useState('')
  const canSend = !!input.trim() && ready
  const canLaunchBackground = canSend && backgroundEligible

  useImperativeHandle(ref, () => ({
    clear: () => setInput(''),
    appendText: (text: string) => setInput((prev) => prev ? `${prev} ${text}` : text),
  }), [])
  const selectData = useMemo(
    () =>
      availableModels.map((group) => ({
        group: group.group,
        items: group.models.map((m) => ({ value: m.id, label: m.label })),
      })),
    [availableModels],
  )

  const submitCurrent = (options?: { headless?: boolean }) => {
    const text = input.trim()
    if (!text || !ready || loading) return
    setInput('')
    onSubmit(text, options)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submitCurrent()
    }
  }

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault()
    submitCurrent()
  }

  return (
    <form onSubmit={handleFormSubmit}>
      <Box
        style={{
          border: '1px solid var(--mantine-color-gray-4)',
          borderRadius: 'var(--mantine-radius-md)',
          overflow: 'hidden',
        }}
      >
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={voiceState === 'recording' ? 'Recording...' : 'Type a message...'}
          disabled={disabled || voiceState === 'recording'}
          autoFocus={autoFocus}
          autosize
          minRows={2}
          maxRows={8}
          variant="unstyled"
          styles={{
            input: {
              padding: '12px 16px',
              paddingRight: voiceState === 'recording' ? 110 : 48,
            },
          }}
          rightSection={
            <VoiceInputButton
              state={voiceState}
              levelHistory={levelHistory}
              onToggle={onVoiceToggle}
              disabled={disabled || loading}
            />
          }
          rightSectionWidth={voiceState === 'recording' ? 100 : 40}
          rightSectionProps={{ style: { alignItems: 'flex-start', paddingTop: 12 } }}
        />

        <Group
          justify="space-between"
          gap="xs"
          px="sm"
          pb="sm"
          pt={0}
        >
          <Group gap="xs">
            <Select
              data={selectData}
              value={selectedModel}
              onChange={(val) => val && onModelChange(val)}
              size="xs"
              variant="filled"
              w={160}
              comboboxProps={{ withinPortal: true }}
              allowDeselect={false}
            />
            <Tooltip label="Attach file (coming soon)">
              <ActionIcon variant="subtle" color="gray" size="md" disabled aria-label="Attach file">
                <Paperclip size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>

          <Group gap="xs">
            <Button
              type="submit"
              size="xs"
              loading={loading}
              disabled={!canSend}
              leftSection={<Send size={14} />}
            >
              Send
            </Button>
            <Tooltip label={canLaunchBackground ? 'Send and run in background' : 'Only available for the first message'}>
              <Button
                size="xs"
                variant="light"
                color="gray"
                disabled={!canLaunchBackground || !canSend}
                leftSection={<Play size={14} />}
                onClick={() => submitCurrent({ headless: true })}
              >
                Background
              </Button>
            </Tooltip>
          </Group>
        </Group>
      </Box>
    </form>
  )
})

export default ChatInputBox
